import { SecureRoute as OktaSecureRoute } from "@okta/okta-react";
import React, { useEffect } from "react";
import { Route, useHistory, useLocation } from "react-router-dom";

import { useValidateAccess } from "../../api/access";
import { useAuth } from "../../contexts/AuthContext";
import { RouteLoadingFallback } from "../RouteLoadingFallback";

type SecureRouteProps = React.ComponentProps<typeof OktaSecureRoute> & {
  unprotected?: boolean;
};

export const SecureRoute: React.FC<SecureRouteProps> = ({
  unprotected,
  ...props
}) => {
  const { oktaAuth } = useAuth();
  const history = useHistory();
  const { pathname, search } = useLocation();
  const accessToken = new URLSearchParams(search).get("access");
  const sessionHint = hasSessionHint();

  const {
    data: accessData,
    isPending: isLoading,
    mutate: validateAccess,
  } = useValidateAccess();

  useEffect(() => {
    if (accessToken || sessionHint) {
      validateAccess(
        accessToken != null
          ? { token: accessToken, path: pathname }
          : { path: pathname }
      );
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Strip ?access= from the URL once the mutation settles (valid or not).
  // The token must never linger in the address bar or browser history.
  useEffect(() => {
    if (accessToken && accessData !== undefined) {
      history.replace(pathname);
    }
  }, [accessToken, accessData, history, pathname]);

  const needsValidation = !!accessToken || sessionHint;
  if (isLoading || (needsValidation && !accessData)) {
    return <RouteLoadingFallback />;
  }

  if (accessData?.valid || unprotected) {
    return <Route {...props} />;
  }

  // No Okta auth — local development.
  if (!oktaAuth) {
    return <Route {...props} />;
  }

  return <OktaSecureRoute {...props} />;
};

const hasSessionHint = (): boolean =>
  document.cookie
    .split(";")
    .some((c) => c.trim().startsWith("playground_session_hint="));
